import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  Space,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Tooltip,
  Alert,
  Tabs,
  Upload,
  Dropdown,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  HistoryOutlined,
  SyncOutlined,
  CloudDownloadOutlined,
  FileExcelOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { templateService, TemplateFile, CreateTemplateRequest } from '../services/templateService';
import { companyService, Company } from '../services/companyService';
import TemplateEditorModal from './TemplateEditorModal';
import './common.css';
import { useI18n } from '../hooks/useI18n';
import { useAuth } from '../contexts/AuthContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

const TemplateManager: React.FC = () => {
  const { t } = useI18n();
  const { isSuperAdmin } = useAuth();
  const [templates, setTemplates] = useState<TemplateFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateFile | null>(null);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncingTemplate, setSyncingTemplate] = useState<TemplateFile | null>(null);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();
  
  // 备份相关状态
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupRemoteLoading, setBackupRemoteLoading] = useState(false);
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupFilesVisible, setBackupFilesVisible] = useState(false);
  const [syncForm] = Form.useForm();
  
  // 多选相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<TemplateFile[]>([]);
  
  // 公司列表
  const [companies, setCompanies] = useState<Company[]>([]);
  
  /** 公司筛选：「全部」含本租户下未关联公司的模板；指定公司时用数字比较（兼容 API 字符串/数字） */
  const [filterCompanyId, setFilterCompanyId] = useState<number | 'all'>('all');
  const [filterBusinessType, setFilterBusinessType] = useState<string | undefined>(undefined);
  const [filterCustomerId, setFilterCustomerId] = useState<string | undefined>(undefined);
  const toVersionParts = (v: string) =>
    (v || '')
      .toString()
      .split(/[^0-9]+/)
      .filter(Boolean)
      .map(x => Number.parseInt(x, 10));

  const compareVersion = (a: string, b: string) => {
    const pa = toVersionParts(a);
    const pb = toVersionParts(b);
    if (!pa.length && !pb.length) return (a || '').localeCompare(b || '');
    if (!pa.length) return -1;
    if (!pb.length) return 1;
    const len = Math.max(pa.length, pb.length);
    for (let i = 0; i < len; i++) {
      const na = pa[i] ?? 0;
      const nb = pb[i] ?? 0;
      if (na !== nb) return na - nb;
    }
    return 0;
  };

  const templateByCode = useMemo(() => {
    const map = new Map<string, TemplateFile>();
    for (const m of templates) {
      const code = m.template_code;
      if (!code) continue;
      const existing = map.get(code);
      if (!existing || compareVersion(m.version, existing.version) > 0) {
        map.set(code, m);
      }
    }
    return map;
  }, [templates]);

  /** 各 template_code 最新版中，版本号最大者（展示用） */
  const latestTemplateVersion = useMemo(() => {
    const vals = Array.from(templateByCode.values());
    if (!vals.length) return '-';
    const best = vals.reduce((acc, cur) =>
      compareVersion(cur.version ?? '', acc.version ?? '') > 0 ? cur : acc
    );
    return best.version || '-';
  }, [templateByCode]);

  useEffect(() => {
    loadTemplates();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await companyService.getCompanies(currentTenant);
      if (response.success) {
        setCompanies(response.data || []);
      }
    } catch (error) {
      console.error('加载公司列表失败:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await templateService.getTemplates();
      setTemplates(data);
    } catch (error: any) {
      message.error(t('templates.manager.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const templateCompanyIdNum = (t: TemplateFile): number | null => {
    const raw = t.company_id as unknown;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  // 过滤模板数据
  const getFilteredTemplates = () => {
    return templates.filter(template => {
      if (filterCompanyId !== 'all') {
        const tid = templateCompanyIdNum(template);
        if (tid === null || tid !== Number(filterCompanyId)) {
          return false;
        }
      }
      // 过滤业务类型
      if (filterBusinessType !== undefined && filterBusinessType !== '') {
        if (!template.business_type || template.business_type !== filterBusinessType) {
          return false;
        }
      }
      // 过滤客户号
      if (filterCustomerId !== undefined && filterCustomerId !== '') {
        if (!template.customer_id || !template.customer_id.toLowerCase().includes(filterCustomerId.toLowerCase())) {
          return false;
        }
      }
      return true;
    });
  };

  // 清除所有过滤条件
  const handleClearFilters = () => {
    setFilterCompanyId('all');
    setFilterBusinessType(undefined);
    setFilterCustomerId(undefined);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setModalVisible(true);
  };

  const handleEdit = (template: TemplateFile) => {
    setEditingTemplate(template);
    setModalVisible(true);
  };

  const handleView = async (template: TemplateFile) => {
    try {
      const jsonTemplate = await templateService.convertToJsonTemplate(template.id);
      const jsonString = JSON.stringify(jsonTemplate, null, 2);
      
      Modal.info({
        title: `${t('templates.manager.viewTemplate')}: ${template.display_name}`,
        width: '80%',
        content: (
          <div>
            <Alert
              message={t('templates.manager.jsonStructure')}
              description={t('templates.manager.jsonStructureDesc')}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <pre style={{ 
              background: '#f5f5f5', 
              padding: 16, 
              borderRadius: 4,
              maxHeight: '60vh',
              overflow: 'auto'
            }}>
              {jsonString}
            </pre>
          </div>
        ),
      });
    } catch (error: any) {
      message.error(t('templates.manager.detailFailed'));
      console.error(error);
    }
  };

  const handleExport = async (template: TemplateFile) => {
    try {
      const jsonString = await templateService.exportTemplate(template.id);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${template.template_code}.json`;
      a.click();
      URL.revokeObjectURL(url);
      message.success(t('templates.manager.exportSuccess'));
    } catch (error: any) {
      message.error(t('templates.manager.exportFailed'));
      console.error(error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await templateService.deleteTemplate(id);
      message.success(t('templates.manager.deleteSuccess'));
      // 更新本地状态，将模板标记为软删除
      setTemplates(templates.map(t => 
        t.id === id ? { ...t, is_active: false, is_synchronized: false } : t
      ));
    } catch (error: any) {
      message.error(t('templates.manager.deleteFailed'));
      console.error(error);
    }
  };

  const handleForceDelete = async (id: number) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/${id}/force`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('templates.manager.forceDeleteSuccess'));
        // 从列表中移除
        setTemplates(templates.filter(t => t.id !== id));
      } else {
        message.error(t('templates.manager.forceDeleteFailed'));
      }
    } catch (error: any) {
      message.error(t('templates.manager.forceDeleteFailed'));
      console.error(error);
    }
  };

  const handleSave = async (templateData: CreateTemplateRequest) => {
    try {
      if (editingTemplate) {
        await templateService.updateTemplate(editingTemplate.id, templateData);
        message.success(t('templates.manager.updateSuccess'));
      } else {
        await templateService.createTemplate(templateData);
        message.success(t('templates.manager.createSuccess'));
      }
      setModalVisible(false);
      loadTemplates();
    } catch (error: any) {
      message.error(editingTemplate ? t('templates.manager.updateFailed') : t('templates.manager.createFailed'));
      console.error(error);
    }
  };

  const handleImport = () => {
    setImportModalVisible(true);
  };

  const handleImportSubmit = async (values: any) => {
    try {
      const jsonContent = values.json_content;
      let parsedContent;
      
      try {
        parsedContent = JSON.parse(jsonContent);
      } catch (e) {
        message.error(t('templates.manager.jsonFormatError'));
        return;
      }
      
      // 检查是否是批量导入格式 { templateFiles: [...] }
      if (parsedContent.templateFiles && Array.isArray(parsedContent.templateFiles)) {
        // 批量导入
        let successCount = 0;
        let failCount = 0;
        const errors: string[] = [];
        
        for (const templateFile of parsedContent.templateFiles) {
          try {
            // 构造导入数据
            const importData = {
              json_content: JSON.stringify(templateFile.content),
              template_name: templateFile.template_name || templateFile.template_code,
              template_code: templateFile.template_code,
              display_name: templateFile.display_name,
              description: templateFile.description,
              version: templateFile.version || '1.0.0',
              is_active: templateFile.is_active !== undefined ? templateFile.is_active : true,
              created_by: templateFile.created_by || 'admin',
              company_id: templateFile.company_id !== undefined ? templateFile.company_id : undefined,
              business_type: templateFile.business_type || undefined,
              customer_id: templateFile.customer_id || undefined
            };
            
            await templateService.importFromJson(importData);
            successCount++;
          } catch (error: any) {
            failCount++;
            const errorMsg = error.response?.data?.message || error.message || t('common.unknownError');
            errors.push(`${templateFile.template_name}: ${errorMsg}`);
            console.error(`导入失败: ${templateFile.template_name}`, error);
          }
        }
        
        // 显示导入结果
        if (failCount === 0) {
          message.success(t('templates.manager.batchImportSuccess', { count: successCount }));
        } else {
          Modal.error({
            title: t('templates.manager.batchImportCompleted'),
            content: (
              <div>
                <p>{t('templates.manager.successCount', { count: successCount })}</p>
                <p>{t('templates.manager.failCount', { count: failCount })}</p>
                {errors.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontWeight: 'bold' }}>{t('templates.manager.failureDetails')}</p>
                    <div style={{ maxHeight: 200, overflow: 'auto' }}>
                      {errors.map((err, idx) => (
                        <div key={idx} style={{ fontSize: 12, color: '#999' }}>
                          {err}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ),
            width: 600
          });
        }
        
        setImportModalVisible(false);
        importForm.resetFields();
        loadTemplates();
      } else if (parsedContent.content && parsedContent.template_name) {
        // 单个模板导入 - 带完整元数据格式
        try {
          const importData = {
            json_content: JSON.stringify(parsedContent.content),
            template_name: parsedContent.template_name,
            template_code: parsedContent.template_code,
            display_name: parsedContent.display_name || parsedContent.template_name,
            description: parsedContent.description,
            version: parsedContent.version || '1.0.0',
            is_active: parsedContent.is_active !== undefined ? parsedContent.is_active : true,
            created_by: parsedContent.created_by || 'admin'
          };
          
          await templateService.importFromJson(importData);
          message.success(t('templates.manager.importSuccess'));
          setImportModalVisible(false);
          importForm.resetFields();
          loadTemplates();
        } catch (error: any) {
          const errorMsg = error.response?.data?.message || error.message || t('common.unknownError');
          message.error(t('templates.manager.importFailedWithError', { error: errorMsg }));
          console.error('导入失败:', error);
        }
      } else {
        // 单个模板导入 - 纯JSON Schema格式（原有逻辑）
        if (!values.template_name) {
          message.error(t('templates.manager.pleaseEnterTemplateName'));
          return;
        }
        
        const importData = {
          json_content: values.json_content,
          template_name: values.template_name,
          created_by: 'admin'
        };
        
        await templateService.importFromJson(importData);
        message.success(t('templates.manager.importSuccess'));
        setImportModalVisible(false);
        importForm.resetFields();
        loadTemplates();
      }
    } catch (error: any) {
      message.error(t('templates.manager.importFailed'));
      console.error(error);
    }
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      importForm.setFieldsValue({ json_content: content });
    };
    reader.readAsText(file);
    return false; // 阻止自动上传
  };

  const loadAvailableFiles = async () => {
    try {
      setLoadingFiles(true);
      const response = await fetch('/api/default/document-parsing/files');
      const data = await response.json();
      if (data.success && data.data && data.data.files) {
        setAvailableFiles(data.data.files);
      }
    } catch (error) {
      console.error('加载可用文件失败:', error);
      message.error(t('templates.manager.loadFilesFailed'));
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleSync = async (template: TemplateFile) => {
    Modal.confirm({
      title: t('templates.manager.confirmSync'),
      content: template.is_active 
        ? t('templates.manager.syncTemplateToRemote', { name: template.display_name })
        : t('templates.manager.syncDeletedTemplate', { name: template.display_name }),
      okText: t('common.confirm'),
      okButtonProps: template.is_active ? {} : { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          setLoading(true);
          const currentTenant = localStorage.getItem('currentTenant') || 'default';
          const response = await fetch(`/api/${currentTenant}/templates/${template.id}/sync`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          
          const result = await response.json();
          if (result.success) {
            message.success(result.message || t('templates.manager.syncSuccess'));
            
            // 如果是软删除的模板，从列表中移除
            if (!template.is_active) {
              setTemplates(templates.filter(t => t.id !== template.id));
            } else {
              // 否则更新同步状态
              setTemplates(templates.map(t =>
                t.id === template.id ? { ...t, is_synchronized: true } : t
              ));
            }
          } else {
            message.error(result.message || t('templates.manager.syncFailed'));
          }
        } catch (error: any) {
          console.error('同步失败:', error);
          message.error(t('templates.manager.syncFailedWithError', { error: error.message || t('common.unknownError') }));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSyncSubmit = async (values: any) => {
    if (!syncingTemplate) return;
    
    try {
      setLoading(true);
      await templateService.syncDocumentFile(values.filename, 'admin');
      message.success(t('templates.manager.publishSuccess'));
      setSyncModalVisible(false);
      syncForm.resetFields();
      setSyncingTemplate(null);
      loadTemplates();
    } catch (error: any) {
      console.error('同步文档失败:', error);
      if (error.response?.status === 500) {
        message.error(t('templates.manager.publishFailedUnavailable'));
      } else if (error.response?.status === 404) {
        message.error(t('templates.manager.publishFailedNotFound'));
      } else {
        message.error(`${t('templates.manager.publishFailed')}: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // 备份Template文件
  const handleBackupTemplates = async () => {
    setBackupLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/backup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('templates.manager.backupSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('templates.manager.backupFailed'));
      }
    } catch (error) {
      console.error('Failed to backup templates:', error);
      message.error(t('templates.manager.backupFailed'));
    } finally {
      setBackupLoading(false);
    }
  };

  // 备份远程Template文件
  const handleBackupRemoteTemplates = async () => {
    setBackupRemoteLoading(true);
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/backup-remote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        message.success(t('templates.manager.backupRemoteSuccess', { filename: result.data.filename }));
        if (isSuperAdmin) loadBackupFiles();
      } else {
        message.error(t('templates.manager.backupRemoteFailedWithError', { error: result.message }));
      }
    } catch (error) {
      console.error('Failed to backup remote templates:', error);
      message.error(t('templates.manager.backupRemoteFailed'));
    } finally {
      setBackupRemoteLoading(false);
    }
  };

  // 加载备份文件列表
  const loadBackupFiles = async () => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/backup/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const result = await response.json();
      if (result.success) {
        setBackupFiles(result.data);
      }
    } catch (error) {
      console.error('Failed to load backup files:', error);
    }
  };

  // 下载备份文件
  const handleDownloadBackupFile = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/backup/${filename}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      // 创建下载链接
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('templates.manager.fileDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download file:', error);
      message.error(t('templates.manager.fileDownloadFailed'));
    }
  };

  // 下载备份文件为ZIP格式（拆分成多个文件）
  const handleDownloadBackupFileAsZip = async (filename: string) => {
    try {
      const currentTenant = localStorage.getItem('currentTenant') || 'default';
      const response = await fetch(`/api/${currentTenant}/templates/backup/${filename}/zip`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('下载失败');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename.replace('.json', '.zip');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success(t('templates.manager.zipDownloadSuccess'));
    } catch (error) {
      console.error('Failed to download ZIP file:', error);
      message.error(t('templates.manager.zipDownloadFailed'));
    }
  };

  // 显示备份文件列表
  const handleShowBackupFiles = () => {
    setBackupFilesVisible(true);
    loadBackupFiles();
  };

  const handleBatchSync = async () => {
    try {
      setLoading(true);
      // 这里可以添加批量同步的逻辑，比如同步所有未同步的templates
      const unsyncedTemplates = templates.filter(t => !t.is_synchronized);
      if (unsyncedTemplates.length === 0) {
        message.info(t('templates.manager.allPublished'));
        return;
      }
      
      const filenames = unsyncedTemplates.map(t => t.template_name);
      await templateService.batchSyncDocumentFiles(filenames, 'admin');
      message.success(t('templates.manager.batchPublishDone', { count: filenames.length }));
      loadTemplates();
    } catch (error: any) {
      message.error(t('templates.manager.batchSyncFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // 批量删除（软删除）
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('templates.manager.pleaseSelectTemplatesToDelete'));
      return;
    }

    Modal.confirm({
      title: t('templates.manager.confirmBatchDelete'),
      content: t('templates.manager.batchDeleteConfirm', { count: selectedRowKeys.length }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;

        for (const id of selectedRowKeys) {
          try {
            await templateService.deleteTemplate(Number(id));
            successCount++;
          } catch (error) {
            failCount++;
            console.error(`删除模板 ${id} 失败:`, error);
          }
        }

        // 更新本地状态
        setTemplates(templates.map(t =>
          selectedRowKeys.includes(t.id) ? { ...t, is_active: false, is_synchronized: false } : t
        ));

        // 清空选择
        setSelectedRowKeys([]);
        setSelectedTemplates([]);

        if (failCount === 0) {
          message.success(t('templates.manager.batchDeleteSuccess', { count: successCount }));
        } else {
          message.warning(t('templates.manager.batchDeletePartial', { success: successCount, fail: failCount }));
        }
      },
    });
  };

  // 批量强制删除（硬删除）
  const handleBatchForceDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('templates.manager.pleaseSelectTemplatesToForceDelete'));
      return;
    }

    Modal.confirm({
      title: t('templates.manager.confirmBatchForceDelete'),
      content: (
        <div>
          <p style={{ color: 'red', fontWeight: 'bold' }}>
            {t('templates.manager.batchForceDeleteWarning', { count: selectedRowKeys.length })}
          </p>
          <p>{t('templates.manager.confirmThisAction')}</p>
        </div>
      ),
      okText: t('common.confirm'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        let successCount = 0;
        let failCount = 0;
        const currentTenant = localStorage.getItem('currentTenant') || 'default';

        for (const id of selectedRowKeys) {
          try {
            const response = await fetch(`/api/${currentTenant}/templates/${id}/force`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              }
            });

            const result = await response.json();
            if (result.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            failCount++;
            console.error(`强制删除模板 ${id} 失败:`, error);
          }
        }

        // 从列表中移除
        setTemplates(templates.filter(t => !selectedRowKeys.includes(t.id)));

        // 清空选择
        setSelectedRowKeys([]);
        setSelectedTemplates([]);

        if (failCount === 0) {
          message.success(t('templates.manager.batchDeleteSuccess', { count: successCount }));
        } else {
          message.warning(t('templates.manager.batchDeletePartial', { success: successCount, fail: failCount }));
        }
      },
    });
  };

  // 批量同步选中的模板
  const handleBatchSyncSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('templates.manager.pleaseSelectTemplatesToSync'));
      return;
    }

    const deletedCount = selectedTemplates.filter(t => !t.is_active).length;
    const activeCount = selectedTemplates.filter(t => t.is_active).length;

    Modal.confirm({
      title: t('templates.manager.confirmBatchSync'),
      content: (
        <div>
          <p>{t('templates.manager.batchSyncConfirm', { count: selectedRowKeys.length })}</p>
          {activeCount > 0 && <p>{t('templates.manager.activeTemplatesCount', { count: activeCount })}</p>}
          {deletedCount > 0 && (
            <p style={{ color: 'red' }}>
              {t('templates.manager.deletedTemplatesCount', { count: deletedCount })}
            </p>
          )}
        </div>
      ),
      okText: t('common.confirm'),
      okButtonProps: deletedCount > 0 ? { danger: true } : {},
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          setLoading(true);
          let successCount = 0;
          let failCount = 0;
          const currentTenant = localStorage.getItem('currentTenant') || 'default';

          for (const template of selectedTemplates) {
            try {
              const response = await fetch(`/api/${currentTenant}/templates/${template.id}/sync`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`,
                  'Content-Type': 'application/json'
                }
              });

              const result = await response.json();
              if (result.success) {
                successCount++;
              } else {
                failCount++;
              }
            } catch (error) {
              failCount++;
              console.error(`同步模板 ${template.id} 失败:`, error);
            }
          }

          // 更新列表：移除已删除的，更新活跃的同步状态
          const deletedIds = selectedTemplates.filter(t => !t.is_active).map(t => t.id);
          setTemplates(templates.filter(t => !deletedIds.includes(t.id)).map(t =>
            selectedRowKeys.includes(t.id) && t.is_active ? { ...t, is_synchronized: true } : t
          ));

          // 清空选择
          setSelectedRowKeys([]);
          setSelectedTemplates([]);

          if (failCount === 0) {
            message.success(t('templates.manager.batchSyncSuccess', { count: successCount }));
          } else {
            message.warning(t('templates.manager.batchDeletePartial', { success: successCount, fail: failCount }));
          }
        } catch (error: any) {
          message.error(t('templates.manager.batchSyncFailed'));
          console.error(error);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const templateColumns = [
    {
      title: 'Template名称',
      dataIndex: 'display_name',
      key: 'display_name',
      align: 'left' as const,
      sorter: (a: TemplateFile, b: TemplateFile) => a.display_name.localeCompare(b.display_name),
      render: (text: string, record: TemplateFile) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.template_code}
          </div>
        </div>
      ),
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      align: 'left' as const,
      width: 100,
      sorter: (a: TemplateFile, b: TemplateFile) => {
        const parseVersion = (v: string) => {
          const parts = v.split('.').map(Number);
          return parts[0] * 10000 + (parts[1] || 0) * 100 + (parts[2] || 0);
        };
        return parseVersion(a.version) - parseVersion(b.version);
      },
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      align: 'left' as const,
      width: 100,
      sorter: (a: TemplateFile, b: TemplateFile) => Number(a.is_active) - Number(b.is_active),
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('common.active') : t('common.inactive')}
        </Tag>
      ),
    },
    {
      title: t('templates.manager.syncStatus'),
      dataIndex: 'is_synchronized',
      key: 'is_synchronized',
      align: 'left' as const,
      width: 120,
      sorter: (a: TemplateFile, b: TemplateFile) => {
        const aSync = a.is_synchronized ? 1 : 0;
        const bSync = b.is_synchronized ? 1 : 0;
        return aSync - bSync;
      },
      render: (isSynchronized: boolean, record: TemplateFile) => (
        <div>
          <Tag color={isSynchronized ? 'blue' : 'orange'}>
            {isSynchronized ? t('templates.manager.published') : t('templates.manager.unpublished')}
          </Tag>
          {record.last_sync_at && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {new Date(record.last_sync_at).toLocaleString()}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      align: 'left' as const,
      ellipsis: true,
      sorter: (a: TemplateFile, b: TemplateFile) => {
        const aDesc = a.description || '';
        const bDesc = b.description || '';
        return aDesc.localeCompare(bDesc);
      },
      render: (text: string) => text || '-',
    },
    {
      title: '关联公司',
      dataIndex: 'company_id',
      key: 'company_id',
      align: 'left' as const,
      width: 150,
      render: (companyId: number) => {
        if (!companyId) return '-';
        const company = companies.find(c => c.id === companyId);
        return company ? company.company_name : `ID: ${companyId}`;
      },
    },
    {
      title: '业务类型',
      dataIndex: 'business_type',
      key: 'business_type',
      align: 'left' as const,
      width: 120,
      render: (businessType: string) => {
        if (!businessType) return '-';
        const colorMap: Record<string, string> = {
          'CPO': 'blue',
          'SQ': 'green',
          'SO': 'orange',
          'APInvoice': 'purple',
        };
        return (
          <Tag color={colorMap[businessType] || 'default'}>
            {businessType}
          </Tag>
        );
      },
    },
    {
      title: '客户',
      dataIndex: 'customer_id',
      key: 'customer_id',
      align: 'left' as const,
      width: 120,
      render: (customerId: string) => {
        if (!customerId) return '-';
        return customerId;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      align: 'left' as const,
      width: 180,
      sorter: (a: TemplateFile, b: TemplateFile) => {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      align: 'left' as const,
      width: 200,
      fixed: 'right' as const,
      render: (_: any, record: TemplateFile) => {
        const deleteMenuItems = [
          {
            key: 'delete',
            label: t('templates.manager.delete'),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('templates.manager.confirmDeleteTemplate'),
                content: t('templates.manager.softDeleteConfirm'),
                okText: t('common.confirm'),
                cancelText: t('common.cancel'),
                onOk: () => handleDelete(record.id),
              });
            },
          },
          {
            key: 'forceDelete',
            label: t('templates.manager.forceDelete'),
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: t('templates.manager.confirmForceDelete'),
                content: t('templates.manager.forceDeleteWarning'),
                okText: t('common.confirm'),
                okButtonProps: { danger: true },
                cancelText: t('common.cancel'),
                onOk: () => handleForceDelete(record.id),
              });
            },
          },
        ];

        return (
          <Space>
            <Tooltip title={t('templates.manager.tooltipEdit')}>
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Tooltip title={t('templates.manager.tooltipView')}>
              <Button
                type="link"
                icon={<EyeOutlined />}
                onClick={() => handleView(record)}
              />
            </Tooltip>
            <Tooltip title={t('templates.manager.tooltipExport')}>
              <Button
                type="link"
                icon={<DownloadOutlined />}
                onClick={() => handleExport(record)}
              />
            </Tooltip>
            <Tooltip title={t('templates.manager.tooltipPublish')}>
              <Button
                type="link"
                icon={<SyncOutlined />}
                onClick={() => handleSync(record)}
              />
            </Tooltip>
            <Dropdown menu={{ items: deleteMenuItems }} trigger={['click']}>
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const activeTemplates = templates.filter(t => t.is_active);
  const syncedTemplates = templates.filter(t => !!t.is_synchronized);
  const pendingTemplates = templates.filter(t => !t.is_synchronized);

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Row align="middle" justify="start" style={{ flexWrap: 'wrap' }}>
          <Space align="baseline" size={16} style={{ flexWrap: 'wrap' }}>
            <Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined style={{ marginRight: '8px' }} />
              {t('templates.manager.title')}
            </Title>
            <Text strong>{t('templates.manager.company')}</Text>
            <Select
              style={{ width: 280 }}
              showSearch
              optionFilterProp="children"
              placeholder={t('templates.manager.selectCompany')}
              value={filterCompanyId}
              onChange={(v) => setFilterCompanyId(v === 'all' ? 'all' : Number(v))}
            >
              <Select.Option value="all">{t('common.all')}</Select.Option>
              {companies.map(company => (
                <Select.Option key={company.id} value={company.id}>
                  {company.company_name}
                </Select.Option>
              ))}
            </Select>
            <Text type="secondary">
              Version: {latestTemplateVersion}
            </Text>
            <Text type="secondary">{t('templates.manager.subtitle')}</Text>
            <Space style={{ flexWrap: 'wrap' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                {t('templates.manager.createTemplate')}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={backupLoading}
                onClick={handleBackupTemplates}
              >
                {t('templates.manager.backupTemplate')}
              </Button>
              <Button
                icon={<CloudDownloadOutlined />}
                loading={backupRemoteLoading}
                onClick={handleBackupRemoteTemplates}
              >
                {t('templates.manager.backupRemoteTemplate')}
              </Button>
              {isSuperAdmin && (
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleShowBackupFiles}
                >
                  {t('templates.manager.backupFiles')}
                </Button>
              )}
              <Button
                icon={<UploadOutlined />}
                onClick={handleImport}
              >
                {t('templates.manager.importTemplate')}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadTemplates}
                loading={loading}
              >
                {t('common.refresh')}
              </Button>
              <Button
                icon={<SyncOutlined />}
                onClick={handleBatchSync}
              >
                {t('templates.manager.batchPublish')}
              </Button>
            </Space>
          </Space>
        </Row>
      </div>

      <Row gutter={16} align="stretch" style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Card
            style={{
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Space size={48} align="center" wrap>
              <Statistic
                title={t('templates.manager.stat.total')}
                value={templates.length}
                prefix={<FileTextOutlined />}
              />
              <Statistic
                title={t('templates.manager.stat.active')}
                value={activeTemplates.length}
                valueStyle={{ color: '#3f8600' }}
                prefix={<CheckCircleOutlined />}
              />
              <Statistic
                title={t('templates.manager.stat.synced')}
                value={syncedTemplates.length}
                valueStyle={{ color: '#1890ff' }}
                prefix={<SyncOutlined />}
              />
              <Statistic
                title={t('templates.manager.stat.pending')}
                value={pendingTemplates.length}
                valueStyle={{ color: '#faad14' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Space>
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text strong>{t('templates.manager.businessType')}</Text>
              <AutoComplete
                placeholder={t('templates.manager.selectOrInputBusinessType')}
                allowClear
                style={{ width: '100%' }}
                value={filterBusinessType}
                onChange={(value) => setFilterBusinessType(value)}
                options={[
                  { value: 'CPO' },
                  { value: 'SQ' },
                  { value: 'SO' },
                  { value: 'APInvoice' },
                  { value: 'ARInvoice' },
                  { value: 'PO' },
                  { value: 'GRPO' },
                  { value: 'DN' },
                ]}
                filterOption={(inputValue, option) =>
                  option?.value?.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text strong>{t('templates.manager.customer')}</Text>
              <Input
                placeholder={t('templates.manager.inputCustomerId')}
                allowClear
                value={filterCustomerId}
                onChange={(e) => setFilterCustomerId(e.target.value)}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text strong>&nbsp;</Text>
              <Space style={{ width: '100%' }} wrap>
                <Button onClick={handleClearFilters} style={{ flex: 1, minWidth: 100 }}>
                  {t('templates.manager.clearFilters')}
                </Button>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={() => setTemplates([...templates])}
                  style={{ flex: 1, minWidth: 100 }}
                >
                  {t('templates.manager.query')}
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>

        {selectedRowKeys.length > 0 && (
          <div
            style={{
              marginBottom: 16,
              padding: '12px 16px',
              background: '#e6f7ff',
              border: '1px solid #91d5ff',
              borderRadius: 8,
            }}
          >
            <Space wrap>
              <Text strong>{t('templates.manager.selectedItems', { count: selectedRowKeys.length })}</Text>
              <Button icon={<DeleteOutlined />} onClick={handleBatchDelete}>
                {t('templates.manager.batchDelete')}
              </Button>
              <Button danger icon={<DeleteOutlined />} onClick={handleBatchForceDelete}>
                {t('templates.manager.batchForceDelete')}
              </Button>
              <Button icon={<SyncOutlined />} onClick={handleBatchSyncSelected}>
                {t('templates.manager.batchSync')}
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setSelectedRowKeys([]);
                  setSelectedTemplates([]);
                }}
              >
                {t('templates.manager.cancelSelection')}
              </Button>
            </Space>
          </div>
        )}

        <Table
          columns={templateColumns.map(c => ({
            ...c,
            title:
              c.key === 'display_name' ? t('templates.manager.colName') :
              c.key === 'version' ? t('templates.manager.colVersion') :
              c.key === 'is_active' ? t('templates.manager.colStatus') :
              c.key === 'is_synchronized' ? t('templates.manager.colSync') :
              c.key === 'description' ? t('templates.manager.colDescription') :
              c.key === 'created_at' ? t('templates.manager.colCreatedAt') :
              c.key === 'company_id' ? '关联公司' :
              c.key === 'business_type' ? '业务类型' :
              c.key === 'customer_id' ? '客户' :
              c.key === 'actions' ? t('templates.manager.colActions') : c.title,
          }))}
          dataSource={getFilteredTemplates()}
          loading={loading}
          scroll={{ x: 'max-content' }}
          rowKey="id"
          rowClassName={(record) => record.is_active ? '' : 'row-deleted'}
          rowSelection={{
            selectedRowKeys,
            onChange: (selectedKeys: React.Key[], selectedRows: TemplateFile[]) => {
              setSelectedRowKeys(selectedKeys);
              setSelectedTemplates(selectedRows);
            },
            selections: [
              Table.SELECTION_ALL,
              Table.SELECTION_INVERT,
              Table.SELECTION_NONE,
            ],
          }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `${t('common.total')} ${total} ${t('common.items')}`,
          }}
        />
      </Card>

      {/* Template编辑模态框 */}
      <TemplateEditorModal
        visible={modalVisible}
        template={editingTemplate}
        onCancel={() => setModalVisible(false)}
        onSave={handleSave}
      />

      {/* 导入Template模态框 */}
      <Modal
        title={t('templates.manager.importTemplate')}
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        footer={null}
        width={800}
      >
        <Alert
          message={t('templates.manager.supportedImportFormats')}
          description={
            <div style={{ fontSize: '12px' }}>
              <p><strong>{t('templates.manager.jsonSchemaFormat')}</strong>{t('templates.manager.jsonSchemaFormatDesc')}</p>
              <p><strong>{t('templates.manager.singleTemplateFormat')}</strong>{t('templates.manager.singleTemplateFormatDesc')}</p>
              <p><strong>{t('templates.manager.batchImportFormat')}</strong>{t('templates.manager.batchImportFormatDesc')}</p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImportSubmit}
        >
          <Form.Item
            name="template_name"
            label={t('templates.manager.templateName')}
            rules={[{ required: false, message: t('templates.manager.enterTemplateName') }]}
          >
            <Input placeholder="批量导入时可留空" />
          </Form.Item>

          <Form.Item
            name="json_content"
            label={t('templates.manager.jsonContent')}
            rules={[{ required: true, message: t('templates.manager.enterJsonContent') }]}
          >
            <TextArea
              rows={15}
              placeholder={t('templates.manager.jsonContentPlaceholder')}
            />
          </Form.Item>

          <Form.Item label={t('templates.manager.orUploadJson')}>
            <Upload
              beforeUpload={handleFileUpload}
              accept=".json"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>{t('templates.manager.chooseJson')}</Button>
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('templates.manager.import')}
              </Button>
              <Button onClick={() => setImportModalVisible(false)}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 同步文档模态框 */}
      <Modal
        title={t('templates.manager.syncDocTitle')}
        open={syncModalVisible}
        onCancel={() => {
          setSyncModalVisible(false);
          setSyncingTemplate(null);
          syncForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={syncForm}
          layout="vertical"
          onFinish={handleSyncSubmit}
          initialValues={{
            filename: syncingTemplate?.template_name || ''
          }}
        >
          <Alert
            message={t('templates.manager.syncDescTitle')}
            description={t('templates.manager.syncDesc')}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            label={t('templates.manager.templateName')}
            name="template_name"
          >
            <Input disabled value={syncingTemplate?.display_name} />
          </Form.Item>

          <Form.Item
            label={t('templates.manager.docFileName')}
            name="filename"
            rules={[{ required: true, message: t('templates.manager.chooseDocFile') }]}
            extra={
              <Button 
                type="link" 
                size="small" 
                icon={<ReloadOutlined />} 
                onClick={loadAvailableFiles}
                loading={loadingFiles}
              >
                {t('templates.manager.refreshFileList')}
              </Button>
            }
          >
            <Select
              placeholder={t('templates.manager.chooseDocFilePlaceholder')}
              loading={loadingFiles}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={availableFiles.map(file => ({
                value: file,
                label: file
              }))}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {t('templates.manager.startSync')}
              </Button>
              <Button onClick={() => {
                setSyncModalVisible(false);
                setSyncingTemplate(null);
                syncForm.resetFields();
              }}>
                {t('common.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 备份文件列表模态框（仅超级管理员） */}
      {isSuperAdmin && (
        <Modal
          title={t('templates.manager.backupFileList')}
          open={backupFilesVisible}
          onCancel={() => setBackupFilesVisible(false)}
          footer={[
            <Button key="close" onClick={() => setBackupFilesVisible(false)}>
              {t('common.close')}
            </Button>
          ]}
          width={800}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">{t('templates.manager.backupFileListDesc')}</Text>
          </div>

          {backupFiles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              <FileTextOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div>{t('templates.manager.noBackupFiles')}</div>
            </div>
          ) : (
            <Table
              dataSource={backupFiles}
              rowKey="filename"
              pagination={false}
              size="small"
              columns={[
                {
                  title: t('templates.manager.fileName'),
                  dataIndex: 'filename',
                  key: 'filename',
                  render: (filename: string) => (
                    <Space>
                      <FileTextOutlined />
                      <Text code>{filename}</Text>
                    </Space>
                  ),
                },
                {
                  title: t('templates.manager.fileSize'),
                  dataIndex: 'size',
                  key: 'size',
                  render: (size: number) => (
                    <Text type="secondary">{(size / 1024).toFixed(1)} KB</Text>
                  ),
                },
                {
                  title: t('templates.manager.createdAt'),
                  dataIndex: 'created',
                  key: 'created',
                  render: (created: string) => (
                    <Text type="secondary">
                      {new Date(created).toLocaleString()}
                    </Text>
                  ),
                },
                {
                  title: t('templates.manager.actions'),
                  key: 'actions',
                  width: 180,
                  fixed: 'right' as const,
                  render: (_: unknown, record: any) => (
                    <Space size="small">
                      <Button
                        type="link"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadBackupFile(record.filename)}
                      >
                        JSON
                      </Button>
                      <Button
                        type="link"
                        icon={<FileExcelOutlined />}
                        onClick={() => handleDownloadBackupFileAsZip(record.filename)}
                      >
                        ZIP
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />
          )}
        </Modal>
      )}
    </div>
  );
};

export default TemplateManager;
